import { ApiClient, ApiError, FetchResponse, SingularReturnValue } from "@/api/ApiClient";
import { getTenantId, resolveCurrentUserLabel, stampCreatorUser } from "@/utils/Auth";
import { AuthMode, CustomHeaders, ModelPath } from "@aws-amplify/data-schema/runtime";
import { AmplifyFilter, matchesAmplifyFilter, removeNullProperties } from "@utils/Api";
import { CACHEABLE_MODELS, DEFAULT_TTL_MS } from "./cacheConfig";
import { cacheKeyAllByTenant } from "@utils/cache/cacheKey";
import { CacheStore } from "@utils/cache/cacheStore";
import { fetchAllByOwner } from "./paginateAmplify";

type AmplifyGetOptions<FlatModel extends Record<string, unknown> = any> = {
    selectionSet?: ReadonlyArray<ModelPath<FlatModel>>;
    authMode?: AuthMode;
    authToken?: string;
    headers?: CustomHeaders;
};

export type FetchOpts = {
    noCache?: boolean; // ignora cache en esta llamada
};

export class GenericModelApi<TModelName extends keyof typeof ApiClient.models, TDataType extends { id?: string, createdAt?: string }> {
    private cache = new CacheStore('modelsCache/v1');
    private maxFetchs = 5
    private apiMaxFetch = 50
    private createDataFieldName: keyof TDataType = "createdAt"
    /**
     * Modelos que guardan quién creó el registro en `user`. Se enciende en la subclase.
     * Los documentos electrónicos no lo usan: los estampa la lambda `save-edocument`
     * desde el token, que es la única fuente confiable del emisor.
     */
    protected stampsCreatorUser = false
    /**
     * Campo que acompaña a `user` en el sort key compuesto del modelo (`user#fecha` o
     * `user#created`). Se deja sin definir en los modelos que no tienen ese indice.
     *
     * El resolver de AppSync exige que un update que toque una parte de esa llave mande
     * TAMBIEN la otra, y valida contra el input de la mutacion, no contra la fila guardada:
     * sin `user` falla con "you must provide all fields for the key". Como casi todos los
     * flujos de cobro mandan `fecha` (la necesitan para `clientId#fecha`), `update()`
     * completa `user` con el usuario actual cuando el llamador no lo trae.
     */
    protected userSortKeyDateField?: "fecha" | "created"

    constructor(protected modelName: TModelName, protected listPropLabel: keyof TDataType, protected sortDirection?: "ASC" | "DESC", protected sortProp?: keyof TDataType, protected byTenantQueryName?: string) {
    }

    protected get model() {
        return ApiClient.models[this.modelName]
    }

    protected setCreateDataFieldName(createDataFieldName: keyof TDataType) {
        this.createDataFieldName = createDataFieldName
    }

    async find(
        amplifyClientFilter?: AmplifyFilter,
        selectionSet?: any): Promise<TDataType> {

        const { items = [] } = await this.fetchAll(amplifyClientFilter, "", 1, selectionSet);
        return items[0] || {}
    }

    async optionsList(filters: any = {}, labelGenerator?: (item: TDataType) => string, valueProp: keyof TDataType = "id"): Promise<Record<string, string>> {
        const { items = [] } = await this.fetchAll(filters, "", 5000);

        return items.filter(i => !!i.id).reduce((a, v) => {
            let label = `${v[this.listPropLabel] || ''}`;
            if (labelGenerator) {
                const generateLabel = labelGenerator(v);
                label = generateLabel || label;
            }
            return {
                ...a,
                [`${v[valueProp]}`]: label
            }
        }, {});
    }

    async getNextCode(field: keyof TDataType): Promise<Number> {
        const { items = [] } = await this.fetchAll({}, "", 1000);
        const currentMaxCode = items.reduce((a, v) => {
            const cod = Number(v[field]);
            if (isNaN(cod)) {
                return a;
            } else {
                return Math.max(a, cod);
            }
        }, Number(0))

        return (currentMaxCode || 0) + 1
    }

    async fetchAll(
        amplifyClientFilter?: AmplifyFilter,
        token?: string,
        limit = 20,
        selectionSet?: any,
        opts: FetchOpts = {},
        sortDireccion: string = this.sortDirection
    ): Promise<FetchResponse<TDataType>> {
        const byTenantQueryName = this.byTenantQueryName || `list${this.modelName}ByTenant`;
        if (!this.model[byTenantQueryName]) {
            return this.fetchAllByList(limit, token, limit, selectionSet)
        } else {
            return this.fetchAllByTenant(byTenantQueryName, amplifyClientFilter, token, limit, selectionSet, opts, sortDireccion)
        }
    }

    async fetchAllByTenant(
        byTenantQueryName: string,
        amplifyClientFilter?: AmplifyFilter,
        token?: string,
        limit = 20,
        selectionSet?: any,
        opts: FetchOpts = {},
        sortDireccion: string = this.sortDirection,
    ): Promise<FetchResponse<TDataType>> {
        const tenantId = await getTenantId();
        const policy = CACHEABLE_MODELS[this.modelName];
        const key = cacheKeyAllByTenant(this.modelName, tenantId, amplifyClientFilter);

        const fetchFunction = this.model[byTenantQueryName];

        if (policy?.enabled && !opts.noCache) {

            const hit = this.cache.get<TDataType[]>(key);
            if (hit) return { items: hit };

            const data = await fetchAllByOwner<TDataType>((nextToken) => fetchFunction({
                owner: tenantId,
            }, {
                sortDirection: sortDireccion,
                limit: 1000,
                nextToken,
                selectionSet: selectionSet,
            }),
                this.modelName,
                amplifyClientFilter
            );

            const ttl = policy.ttlMs ?? DEFAULT_TTL_MS;
            this.cache.set(key, data, ttl);

            return { items: data };
        }

        const collected: TDataType[] = [];
        let nextToken: string | undefined = token;

        let requestCount = 0

        while (collected.length < limit && this.maxFetchs >= requestCount) {
            const pageSize = Math.min(100, limit - collected.length);

            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
            }, {
                sortDirection: sortDireccion,
                limit: this.apiMaxFetch,
                nextToken,
                selectionSet: selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener facturas por tenant", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: TDataType) => matchesAmplifyFilter(item, amplifyClientFilter));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
            requestCount++
        }

        return { items: collected, nextToken };
    }

    /**
     * Lista registros por un `owner` (tenantId) explicito, sin depender del tenant
     * del usuario logueado. Pensado para operaciones admin (grupo "admin") que
     * necesitan ver datos de OTRA suscripcion. Usa el mismo GSI `list<Model>ByTenant`.
     */
    async fetchAllByOwnerId(
        ownerId: string,
        amplifyClientFilter?: AmplifyFilter,
        selectionSet?: any,
        sortDireccion: string = this.sortDirection,
    ): Promise<FetchResponse<TDataType>> {
        const byTenantQueryName = this.byTenantQueryName || `list${this.modelName}ByTenant`;
        const fetchFunction = this.model[byTenantQueryName];
        if (!fetchFunction) {
            throw new ApiError(`No existe el query por tenant para ${String(this.modelName)}`, "API_ERROR");
        }

        const data = await fetchAllByOwner<TDataType>((nextToken) => fetchFunction({
            owner: ownerId,
        }, {
            sortDirection: sortDireccion,
            limit: 1000,
            nextToken,
            selectionSet,
        }),
            this.modelName,
            amplifyClientFilter
        );

        return { items: data };
    }

    async fetchAllByReferenceNumber(referenceNumber: string, filters: any = {}, options?: any, token?: string): Promise<FetchResponse<TDataType>> {
        const { selectionSet, limit = 1000, sortDirection = "ASC" } = options
        const tenantId = await getTenantId();
        const byTenantQueryName = `list${this.modelName}ByReferenceNumberTenant`;
        if (!this.model[byTenantQueryName]) {
            throw new ApiError(`No existe el query oara obtener los documentos por Reference Number -  ${String(this.modelName)} `, "API_ERROR");
        }
        const fetchFunction = this.model[byTenantQueryName];

        const collected: TDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
                "reference_number": { eq: referenceNumber }
            }, {
                sortDirection,
                limit,
                nextToken,
                selectionSet,
            });

            if (errors) {
                throw new ApiError(`Error al obtener los  ${String(this.modelName)} por reference number tenant`, "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: TDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };

    }


    async fetchAllByPeriod(byPeriodTenantQueryName: string, startData: string, endData: string, filters: any = {}, token?: string, options: any): Promise<FetchResponse<TDataType>> {
        const { selectionSet, limit = 1000, sortDirection = "ASC" } = options
        const tenantId = await getTenantId();
        const fetchFunction = this.model[byPeriodTenantQueryName];

        const collected: TDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
                [this.createDataFieldName]: { between: [startData, endData] }
            }, {
                sortDirection,
                limit,
                nextToken,
                selectionSet,
            });

            if (errors) {
                throw new ApiError(`Error al obtener los  ${String(this.modelName)} por period tenant`, "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: TDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };

    }

    async fetchAllByListNoTenant(filters: any = {}, token?: string, limit: number = 20, selectionSet?: any): Promise<FetchResponse<TDataType>> {
        return await this.fetchAllByList(filters, token, limit, selectionSet, false)
    }

    async fetchAllByList(filters: any = {}, token?: string, limit: number = 20, selectionSet?: any, useTenantFilter = true): Promise<FetchResponse<TDataType>> {
        const tenantId = await getTenantId();
        let filterObject;
        if (useTenantFilter) {
            filterObject = {
                and: [
                    { owner: { eq: tenantId } },
                    ...(Object.keys(filters).length > 0 ? [filters] : [])
                ]
            };
        } else {
            filterObject = {
                and: [
                    ...(Object.keys(filters).length > 0 ? [filters] : [])
                ]
            };
        }

        const { data, errors, nextToken } = await this.model.list({
            filter: filterObject,
            limit,
            nextToken: token,
            selectionSet: selectionSet,
        });

        if (errors) {
            throw new ApiError(`Error al obtener los datos de ${String(this.modelName)}`, "API_ERROR", errors);
        }

        return { items: data, nextToken };
    }

    async get(id: string, options?: AmplifyGetOptions): Promise<TDataType> {
        const { data, errors } = await this.model.get({ id }, options);

        if (errors) {
            throw new ApiError(`Errores al obtener ${String(this.modelName)}`, "API_ERROR", errors);
        }

        return data || {} as TDataType;
    }

    async save(sequence: TDataType): Promise<TDataType> {
        if (sequence?.id) {
            return await this.update(sequence.id, sequence)
        } else {
            return await this.create(sequence)
        }
    }

    async create(input: TDataType): Promise<TDataType> {
        const stamped = this.stampsCreatorUser ? await stampCreatorUser(input) : input;
        const modelData = removeNullProperties(stamped) as any;
        const { data, errors } = await this.model.create(modelData);

        if (errors) {
            throw new ApiError(`Errores al crear ${String(this.modelName)}`, "API_ERROR", errors);
        }

        this.invalidateAllByTenant()
        this.clearCache()
        return data || {} as TDataType;
    }

    async update(id: string, input: Partial<Omit<TDataType, 'id'>>, allowNulls = false): Promise<TDataType> {
        const { owner, ...rest } = allowNulls ? input : removeNullProperties(input) as any;
        // Se hace aqui y no en `save()` para cubrir tambien a quienes llaman `update()`
        // directamente, que son la mayoria (pagos, recibos, marcar como pagada). Solo cuando
        // el input toca la otra mitad de la llave: si no, el resolver no valida nada y
        // estampar `user` pisaria al creador sin motivo.
        const dateField = this.userSortKeyDateField;
        if (dateField && dateField in rest && !("user" in rest)) {
            rest.user = await resolveCurrentUserLabel();
        }
        const { data, errors } = await this.model.update({ id, ...rest });

        if (errors) {
            throw new ApiError(`Errores al actualizar ${String(this.modelName)}`, "API_ERROR", errors);
        }

        this.invalidateAllByTenant()
        this.clearCache()
        return data || {} as TDataType;
    }

    async delete(id: string): Promise<SingularReturnValue<TDataType>> {
        const response = await this.model.delete({ id });

        if (response.errors) {
            throw new ApiError(`Errores al eliminar ${String(this.modelName)}`, "API_ERROR", response.errors);
        }
        this.invalidateAllByTenant()
        this.clearCache()
        return response;
    }

    clearCache() {
        this.cache.clearAll();
    }

    async invalidateAllByTenant() {
        const tenantId = await getTenantId();

        const prefix = `allByTenant:${this.modelName}:${tenantId}`;
        const keys = this.cache.getAvailableKeys();

        //remove from localstorage
        const cacheStorage = Object.keys(localStorage)
        const keysToDeleteStore = cacheStorage.filter((k) => k.startsWith(`${this.cache.getNameSpace()}:${prefix}`));
        keysToDeleteStore.forEach(k => {
            try { localStorage.removeItem(k); } catch { }

        })

        //remove from cache
        const keysToDelete = keys.filter((k) => k.startsWith(prefix));
        keysToDelete.forEach(k => this.cache.del(k))
    }
}
